import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import BoardPacketModal from './BoardPacketModal';
import {
  CheckCircle2, Circle, ChevronLeft, Printer, Share2, Copy, RefreshCw,
  ClipboardList, Coffee, Rocket, Lock, Download, FileText, Palette, Image as ImageIcon,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });

const TYPE_META = {
  task: { color: '#1D4ED8', label: 'Task', icon: ClipboardList },
  review: { color: '#059669', label: 'Review', icon: RefreshCw },
  brain_break: { color: '#D97706', label: 'Brain Break', icon: Coffee },
  launch: { color: '#DB2777', label: 'Launch', icon: Rocket },
};

export default function NonprofitProgress() {
  const { projectId, token } = useParams();
  const navigate = useNavigate();
  const isAdmin = !!projectId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [curriculum, setCurriculum] = useState([]);
  const [phases, setPhases] = useState([]);
  const [readOnly, setReadOnly] = useState(true);
  const [openDay, setOpenDay] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [savingDay, setSavingDay] = useState(false);
  // sharing (admin only)
  const [share, setShare] = useState({ share_token: null, share_enabled: false, shared_emails: [] });
  const [emailsText, setEmailsText] = useState('');
  const [savingShare, setSavingShare] = useState(false);
  const [showPacket, setShowPacket] = useState(false);
  // branding (admin only)
  const [branding, setBranding] = useState({ primary_color: '#1D4ED8', accent_color: '#7C3AED', has_logo: false });
  const [logoVer, setLogoVer] = useState(0);
  const [savingBrand, setSavingBrand] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      if (isAdmin) {
        const r = await api.get(`/api/admin/nonprofit-accelerator/projects/${projectId}`);
        setProject(r.data.project);
        setCurriculum(r.data.curriculum || []);
        setPhases(r.data.phases || []);
        setReadOnly(false);
        const b = r.data.project.branding || {};
        setBranding({ primary_color: b.primary_color || '#1D4ED8', accent_color: b.accent_color || '#7C3AED', has_logo: !!b.logo_path });
        try {
          const sr = await api.get(`/api/admin/nonprofit-accelerator/projects/${projectId}/share`);
          setShare(sr.data);
          setEmailsText((sr.data.shared_emails || []).join('\n'));
        } catch (_) {}
      } else {
        const r = await api.get(`/api/nonprofit-progress/${token}`);
        setProject(r.data.project);
        setCurriculum(r.data.curriculum || []);
        setPhases(r.data.phases || []);
        setReadOnly(true); // shared link is always view-only; owners edit on the admin route
      }
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) { navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`); return; }
      setError(e.response?.data?.detail || 'Could not load this progress page.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, projectId, token, navigate]);

  useEffect(() => { load(); }, [load]);

  const days = project?.days || {};
  const totalTracked = curriculum.filter((d) => d.type !== 'brain_break').length;
  const completedCount = Object.values(days).filter((v) => v?.completed).length;
  const overallPct = totalTracked ? Math.round((completedCount / totalTracked) * 100) : 0;

  const toggleComplete = async (day, next) => {
    if (readOnly) return;
    try {
      await api.patch(`/api/admin/nonprofit-accelerator/projects/${projectId}/days/${day}`, { completed: next });
      setProject((p) => ({ ...p, days: { ...(p.days || {}), [String(day)]: { ...((p.days || {})[String(day)] || {}), completed: next } } }));
    } catch (e) { toast.error('Could not update'); }
  };

  const openEditor = (day) => {
    setOpenDay(day);
    setEditNotes((days[String(day)] || {}).notes || '');
  };

  const saveNotes = async () => {
    if (readOnly || openDay == null) return;
    setSavingDay(true);
    try {
      await api.patch(`/api/admin/nonprofit-accelerator/projects/${projectId}/days/${openDay}`, { notes: editNotes });
      setProject((p) => ({ ...p, days: { ...(p.days || {}), [String(openDay)]: { ...((p.days || {})[String(openDay)] || {}), notes: editNotes } } }));
      toast.success('Saved');
      setOpenDay(null);
    } catch (e) { toast.error('Could not save'); }
    finally { setSavingDay(false); }
  };

  const saveShare = async (enabled) => {
    setSavingShare(true);
    try {
      const emails = emailsText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${projectId}/share`, { emails, enabled });
      setShare(r.data);
      setEmailsText((r.data.shared_emails || []).join('\n'));
      toast.success(enabled ? 'Private share link is live' : 'Sharing turned off');
    } catch (e) { toast.error('Could not update sharing'); }
    finally { setSavingShare(false); }
  };

  const shareUrl = share.share_token ? `${window.location.origin}/np/progress/${share.share_token}` : '';

  const saveBranding = async (opts = {}) => {
    setSavingBrand(true);
    try {
      const fd = new FormData();
      fd.append('primary_color', branding.primary_color);
      fd.append('accent_color', branding.accent_color);
      if (opts.logoFile) fd.append('logo', opts.logoFile);
      if (opts.removeLogo) fd.append('remove_logo', '1');
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${projectId}/branding`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBranding((b) => ({ ...b, has_logo: r.data.branding.has_logo }));
      setLogoVer((v) => v + 1);
      toast.success('Branding saved - it will appear on your PDFs');
    } catch (e) { toast.error('Could not save branding'); }
    finally { setSavingBrand(false); }
  };

  const downloadPdf = async (kind) => {
    const map = { plan: 'plan-pdf', packet: 'board-packet-pdf' };
    toast.info(`Preparing your ${kind === 'packet' ? 'board packet' : '90-day plan'} PDF…`);
    try {
      const r = await api.get(`/api/admin/nonprofit-accelerator/projects/${projectId}/${map[kind]}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${(project?.name || 'nonprofit').replace(/\s+/g, '_')}_${kind === 'packet' ? 'board_packet' : '90_day_plan'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]"><RefreshCw className="animate-spin text-[#1D4ED8]" size={28} /></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
        <div className="max-w-md text-center bg-white border border-[#E2E8F0] rounded-2xl p-8" data-testid="npp-error">
          <Lock size={32} className="mx-auto text-[#CBD5E1] mb-3" />
          <h1 className="text-lg font-bold text-[#0A0F1C] mb-1">Not available</h1>
          <p className="text-sm text-[#64748B]">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] print:bg-white" data-testid="nonprofit-progress-page">
      <style>{`@media print { .no-print { display: none !important; } }`}</style>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            {isAdmin && (
              <a href="/admin/nonprofit-accelerator" className="no-print inline-flex items-center gap-1 text-sm font-semibold text-[#475569] hover:text-[#0A0F1C] mb-2" data-testid="npp-back"><ChevronLeft size={16} /> Back to Accelerator</a>
            )}
            <div className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-wider text-[#1D4ED8] mb-1"><Rocket size={13} /> 90-Day Launch Progress</div>
            <h1 className="text-2xl sm:text-3xl font-black text-[#0A0F1C]" data-testid="npp-name">{project?.name}</h1>
            {project?.mission && <p className="text-sm text-[#475569] mt-1 max-w-2xl">{project.mission}</p>}
          </div>
          <div className="flex items-center gap-2 no-print flex-wrap">
            {isAdmin && (
              <>
                <button type="button" onClick={() => downloadPdf('plan')} data-testid="npp-export-plan" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold border border-[#1D4ED8] text-[#1D4ED8] rounded-lg hover:bg-[#EFF6FF]"><Download size={13} /> 90-Day Plan PDF</button>
                <button type="button" onClick={() => setShowPacket(true)} data-testid="npp-export-packet" title="Pick sections, AI-draft any missing pieces, then download or email a board-ready PDF" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold border border-[#7C3AED] text-[#7C3AED] rounded-lg hover:bg-[#F5F3FF]"><FileText size={13} /> Board Packet</button>
              </>
            )}
            <button type="button" onClick={() => window.print()} data-testid="npp-print" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold border border-[#E2E8F0] text-[#475569] rounded-lg hover:bg-white"><Printer size={13} /> Print / Present</button>
          </div>
        </div>

        {/* Overall progress */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 mb-5" data-testid="npp-overall">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-[#0A0F1C]">Overall progress</span>
            <span className="text-sm font-black text-[#1D4ED8]">{completedCount}/{totalTracked} · {overallPct}%</span>
          </div>
          <div className="h-2.5 bg-[#E2E8F0] rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#1D4ED8] to-[#7C3AED] rounded-full transition-all" style={{ width: `${overallPct}%` }} /></div>
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            {phases.map((ph) => {
              const pdays = curriculum.filter((d) => d.phase === ph.phase && d.type !== 'brain_break');
              const done = pdays.filter((d) => days[String(d.day)]?.completed).length;
              const pct = pdays.length ? Math.round((done / pdays.length) * 100) : 0;
              return (
                <div key={ph.phase} className="rounded-xl border border-[#E2E8F0] p-3" data-testid={`npp-phase-${ph.phase}`}>
                  <div className="text-[12px] font-black text-[#0A0F1C]">{ph.title}</div>
                  <div className="text-[10px] text-[#64748B] mb-1.5">Days {ph.start}-{ph.end}</div>
                  <div className="h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden"><div className="h-full bg-[#1D4ED8] rounded-full" style={{ width: `${pct}%` }} /></div>
                  <div className="text-[10px] text-[#94A3B8] mt-1">{done}/{pdays.length} complete</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Share panel (admin only) */}
        {isAdmin && (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 mb-5 no-print" data-testid="npp-share-panel">
            <div className="flex items-center gap-2 mb-1"><Share2 size={15} className="text-[#7C3AED]" /><span className="text-sm font-bold text-[#0A0F1C]">Share with your board (private)</span></div>
            <p className="text-xs text-[#64748B] mb-3">Add board members' emails, then send them the private link below. They'll need to sign in, and only whitelisted people can view. It stays read-only for them — you're the only one who can edit.</p>
            <textarea value={emailsText} onChange={(e) => setEmailsText(e.target.value)} data-testid="npp-share-emails" rows={3} placeholder="board.member@example.com, another@example.com" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm mb-2" />
            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" onClick={() => saveShare(true)} disabled={savingShare} data-testid="npp-share-enable" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold bg-[#1D4ED8] text-white rounded-lg hover:opacity-90 disabled:opacity-60">{savingShare ? <RefreshCw size={13} className="animate-spin" /> : <Share2 size={13} />} {share.share_enabled ? 'Update sharing' : 'Enable private link'}</button>
              {share.share_enabled && <button type="button" onClick={() => saveShare(false)} disabled={savingShare} data-testid="npp-share-disable" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold border border-[#E2E8F0] text-[#475569] rounded-lg hover:bg-[#F8FAFC]">Turn off</button>}
            </div>
            {share.share_enabled && shareUrl && (
              <div className="mt-3 flex items-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2">
                <span className="text-xs text-[#475569] truncate flex-1" data-testid="npp-share-url">{shareUrl}</span>
                <button type="button" onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success('Link copied'); }} data-testid="npp-share-copy" className="inline-flex items-center gap-1 text-xs font-bold text-[#1D4ED8] hover:underline"><Copy size={12} /> Copy</button>
              </div>
            )}
          </div>
        )}

        {/* Branding panel (admin only) - logo + colors used on the PDFs */}
        {isAdmin && (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 mb-5 no-print" data-testid="npp-branding-panel">
            <div className="flex items-center gap-2 mb-1"><Palette size={15} className="text-[#7C3AED]" /><span className="text-sm font-bold text-[#0A0F1C]">Branding (logo &amp; colors)</span></div>
            <p className="text-xs text-[#64748B] mb-3">Upload your logo and set brand colors. These appear at the top of your 90-Day Plan PDF and Board Packet.</p>
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex flex-col items-center gap-2">
                <div className="w-28 h-20 rounded-lg border border-dashed border-[#CBD5E1] bg-[#F8FAFC] flex items-center justify-center overflow-hidden">
                  {branding.has_logo ? (
                    <img key={logoVer} src={`${API_URL}/api/admin/nonprofit-accelerator/projects/${projectId}/branding/logo?v=${logoVer}`} alt="logo" className="max-w-full max-h-full object-contain" data-testid="npp-logo-preview" />
                  ) : (
                    <ImageIcon size={22} className="text-[#CBD5E1]" />
                  )}
                </div>
                <label data-testid="npp-logo-upload" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-dashed border-[#CBD5E1] text-[#475569] rounded-lg hover:bg-[#F8FAFC] cursor-pointer">
                  <ImageIcon size={12} /> {branding.has_logo ? 'Replace logo' : 'Upload logo'}
                  <input type="file" className="hidden" accept="image/png,image/jpeg,image/webp" onChange={(e) => { const f = e.target.files?.[0]; if (f) saveBranding({ logoFile: f }); e.target.value = ''; }} />
                </label>
                {branding.has_logo && <button type="button" onClick={() => saveBranding({ removeLogo: true })} data-testid="npp-logo-remove" className="text-[11px] font-bold text-red-500 hover:underline">Remove logo</button>}
              </div>
              <div className="flex-1 min-w-[220px] space-y-3">
                <div className="flex items-center gap-3">
                  <label className="text-xs font-bold text-[#475569] w-28">Primary color</label>
                  <input type="color" value={branding.primary_color} onChange={(e) => setBranding((b) => ({ ...b, primary_color: e.target.value }))} data-testid="npp-color-primary" className="w-10 h-8 rounded border border-[#E2E8F0]" />
                  <span className="text-xs text-[#64748B]">{branding.primary_color}</span>
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-xs font-bold text-[#475569] w-28">Accent color</label>
                  <input type="color" value={branding.accent_color} onChange={(e) => setBranding((b) => ({ ...b, accent_color: e.target.value }))} data-testid="npp-color-accent" className="w-10 h-8 rounded border border-[#E2E8F0]" />
                  <span className="text-xs text-[#64748B]">{branding.accent_color}</span>
                </div>
                <button type="button" onClick={() => saveBranding()} disabled={savingBrand} data-testid="npp-branding-save" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold bg-[#1D4ED8] text-white rounded-lg hover:opacity-90 disabled:opacity-60">{savingBrand ? <RefreshCw size={13} className="animate-spin" /> : <Palette size={13} />} Save colors</button>
              </div>
            </div>
          </div>
        )}


        {/* Phase-by-phase days */}
        <div className="space-y-6">
          {phases.map((ph) => (
            <div key={ph.phase} data-testid={`npp-section-${ph.phase}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#1D4ED8] text-white text-[12px] font-black">{ph.phase}</span>
                <div><div className="text-base font-black text-[#0A0F1C]">{ph.title}</div><div className="text-[10px] text-[#64748B]">Days {ph.start}-{ph.end} · {ph.subtitle}</div></div>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
                {curriculum.filter((d) => d.phase === ph.phase).map((d) => {
                  const entry = days[String(d.day)] || {};
                  const done = !!entry.completed;
                  const meta = TYPE_META[d.type] || TYPE_META.task;
                  const notes = (entry.notes || '').trim();
                  const isBreak = d.type === 'brain_break';
                  return (
                    <div key={d.day} data-testid={`npp-day-${d.day}`} className="flex items-start gap-3 px-4 py-3 border-b border-[#F1F5F9] last:border-0">
                      <button type="button" disabled={readOnly || isBreak} onClick={() => toggleComplete(d.day, !done)} data-testid={`npp-toggle-${d.day}`} className={`mt-0.5 shrink-0 ${readOnly || isBreak ? 'cursor-default' : 'cursor-pointer'}`} aria-label="Toggle complete">
                        {done ? <CheckCircle2 size={18} className="text-emerald-600" /> : <Circle size={18} style={{ color: meta.color }} />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[11px] font-black uppercase tracking-wider" style={{ color: meta.color }}>Day {d.day}</span>
                          <span className="text-sm font-bold text-[#0A0F1C]">{d.title}</span>
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${meta.color}14`, color: meta.color }}>{meta.label}</span>
                        </div>
                        {notes ? (
                          <p className="text-xs text-[#475569] mt-1 whitespace-pre-wrap line-clamp-4">{notes}</p>
                        ) : (
                          !isBreak && <p className="text-xs text-[#94A3B8] mt-1 italic">No notes yet</p>
                        )}
                      </div>
                      {!readOnly && !isBreak && (
                        <button type="button" onClick={() => openEditor(d.day)} data-testid={`npp-edit-${d.day}`} className="no-print text-[11px] font-bold text-[#1D4ED8] hover:underline shrink-0">Edit</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit modal (admin only) */}
      {openDay != null && !readOnly && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 no-print" data-testid="npp-edit-modal" onClick={() => setOpenDay(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#0A0F1C] mb-2">Day {openDay} notes</h3>
            <textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} data-testid="npp-edit-notes" rows={8} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm mb-3" />
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => setOpenDay(null)} className="px-3 py-2 text-xs font-bold text-[#475569]">Cancel</button>
              <button type="button" onClick={saveNotes} disabled={savingDay} data-testid="npp-edit-save" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold bg-[#1D4ED8] text-white rounded-lg hover:opacity-90 disabled:opacity-60">{savingDay ? <RefreshCw size={13} className="animate-spin" /> : null} Save</button>
            </div>
          </div>
        </div>
      )}
      {showPacket && isAdmin && (
        <BoardPacketModal projectId={projectId} projectName={project?.name} onClose={() => setShowPacket(false)} />
      )}
    </div>
  );
}
